import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, Star, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface Testimonial {
  name: string;
  rating: number;
  comment: string;
  date: string;
  verified?: boolean;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
  category?: string;
  profileName?: string;
}

// Category-specific testimonial templates
const getCategoryTestimonials = (category?: string, profileName?: string): Testimonial[] => {
  const name = profileName || 'este centro';
  
  const testimonialsByCategory: Record<string, Testimonial[]> = {
    gym: [
      {
        name: "Roberto Sánchez",
        rating: 5,
        comment: `Llevo 8 meses entrenando en ${name} y la transformación ha sido increíble. Las máquinas son de última generación, tienen zona de peso libre muy completa y los monitores siempre están disponibles para corregir posturas. He ganado 6kg de masa muscular limpia.`,
        date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Elena Vidal",
        rating: 5,
        comment: `El ambiente en ${name} es muy motivador. A diferencia de otros gimnasios, aquí no hay masificación y puedes entrenar tranquilo. Los vestuarios están siempre limpios y tienen sauna incluida. La inversión merece la pena.`,
        date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Miguel Ángel Torres",
        rating: 4,
        comment: `Buen gimnasio con horario amplio (6am-11pm). La zona cardio es excelente con cintas Technogym y elípticas de calidad. Echo de menos más variedad de mancuernas pesadas, pero para el 90% de usuarios es más que suficiente.`,
        date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Lucía Hernández",
        rating: 5,
        comment: `Como mujer que entrena sola, ${name} me da mucha seguridad. Tienen zona exclusiva femenina, las clases dirigidas son geniales y el personal muy profesional. He perdido 12kg en 6 meses con su programa de nutrición incluido.`,
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    crossfit: [
      {
        name: "Adrián Molina",
        rating: 5,
        comment: `${name} ha cambiado mi concepto del fitness. Los WODs están perfectamente programados, escalan todo según tu nivel y los coaches corrigen cada movimiento. En 4 meses he conseguido mi primer muscle-up y mejorado mis tiempos un 30%.`,
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Cristina Delgado",
        rating: 5,
        comment: `La comunidad en ${name} es lo mejor. Todos nos animamos, nadie juzga tu nivel y los coaches son atletas competidores que saben de lo que hablan. El box está super equipado con Rogue y tienen clases de gimnásticos específicos.`,
        date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Pablo Ruiz",
        rating: 4,
        comment: `Excelente programación y coaches muy cualificados. Las clases de Olympic lifting son fantásticas. El único pero es que en horario de tarde (18-20h) a veces hay que esperar turno. Recomiendo ir por la mañana o al mediodía.`,
        date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Marina Costa",
        rating: 5,
        comment: `Empecé sin saber nada de CrossFit y en ${name} me enseñaron desde cero con mucha paciencia. Ahora compito a nivel regional y todo se lo debo a mis coaches. La preparación para competiciones es increíble.`,
        date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    yoga: [
      {
        name: "Isabel García",
        rating: 5,
        comment: `${name} es mi refugio de paz. Las clases de Vinyasa con María son transformadoras - combina perfectamente la parte física con la meditación. El espacio está decorado con mucho gusto y los materiales (esterillas Manduka, bloques de corcho) son de primera.`,
        date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Daniel Ortega",
        rating: 5,
        comment: `Como ejecutivo estresado, el yoga en ${name} ha sido mi salvación. Ofrecen desde Yin Yoga muy relajante hasta Ashtanga intenso. Los retiros de fin de semana que organizan son una experiencia única. Mi flexibilidad y gestión del estrés han mejorado enormemente.`,
        date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Carmen Navarro",
        rating: 4,
        comment: `Muy buen estudio con profesores formados en India. Las clases de yoga prenatal me ayudaron mucho durante el embarazo. Solo sugeriría ampliar horarios de fin de semana, pero la calidad de la enseñanza es impecable.`,
        date: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Andrés Serrano",
        rating: 5,
        comment: `Probé muchos estudios antes de encontrar ${name}. La diferencia está en la atención personalizada - grupos reducidos de máximo 12 personas y ajustes individuales. He superado mi dolor de espalda crónico gracias a la terapia de yoga que ofrecen.`,
        date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    pilates: [
      {
        name: "Beatriz Moreno",
        rating: 5,
        comment: `${name} tiene los mejores equipos de Pilates que he probado - Reformers Balanced Body, Cadillac, Chair... Las instructoras tienen formación BASI y Polestar. Mi postura ha mejorado completamente y ya no tengo dolores lumbares después de 3 meses.`,
        date: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Fernando López",
        rating: 5,
        comment: `Como fisioterapeuta, recomiendo ${name} a mis pacientes por su enfoque terapéutico del Pilates. Los grupos son de máximo 4 personas en máquinas y la atención es casi personalizada. Resultados garantizados para problemas de espalda.`,
        date: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Silvia Ramírez",
        rating: 4,
        comment: `Excelente studio de Pilates con clases variadas: mat, reformer, hipopresivos... Las clases de suelo pélvico post-parto fueron fundamentales en mi recuperación. Precio algo elevado pero la calidad lo justifica.`,
        date: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Jorge Martín",
        rating: 5,
        comment: `Empecé Pilates en ${name} por recomendación médica tras una hernia discal. En 6 meses he fortalecido mi core de forma segura y controlada. Las instructoras entienden perfectamente las limitaciones y adaptan cada ejercicio.`,
        date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    dance: [
      {
        name: "Alejandra Ruiz",
        rating: 5,
        comment: `${name} es la mejor academia de baile de la ciudad. Los profesores de salsa y bachata son bailarines profesionales que actúan en espectáculos. En 3 meses pasé de no saber nada a bailar con soltura en cualquier discoteca.`,
        date: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Marcos Gutiérrez",
        rating: 5,
        comment: `Las clases de hip-hop en ${name} son increíbles. Ambiente urbano auténtico, coreografías actuales y profesores que han trabajado con artistas conocidos. Mi coordinación y ritmo han mejorado muchísimo. Además organizan showcases muy profesionales.`,
        date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Patricia Vega",
        rating: 4,
        comment: `Fantástica escuela con gran variedad: ballet, contemporáneo, flamenco, urbano... Los espejos y el parquet son de calidad profesional. Solo mejoraría el sistema de reservas online, pero las clases son excelentes.`,
        date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Ricardo Flores",
        rating: 5,
        comment: `Mis hijos llevan 2 años en ${name} y han ganado varios concursos regionales. La preparación para competiciones es seria y profesional. También tienen clases para adultos donde conocí a mi pareja de baile actual.`,
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    martial_arts: [
      {
        name: "David Chen",
        rating: 5,
        comment: `${name} tiene los mejores instructores de artes marciales. El Sensei de karate es cinturón negro 5º dan y ha formado a campeones nacionales. El tatami es profesional, hay zona de saco y sparring. Mi técnica ha mejorado exponencialmente.`,
        date: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Laura Mendoza",
        rating: 5,
        comment: `Como mujer, buscaba un dojo donde sentirme cómoda y ${name} superó mis expectativas. Las clases de defensa personal son muy prácticas y los instructores muy respetuosos. He ganado confianza y seguridad en mí misma.`,
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Alberto Jiménez",
        rating: 4,
        comment: `Excelente gimnasio de MMA con clases de striking, grappling y BJJ. Los coaches tienen experiencia en competición profesional. Buen ambiente de compañerismo. Sugeriría más horarios de open mat pero la formación es top.`,
        date: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Sofía Blanco",
        rating: 5,
        comment: `Mi hijo de 8 años entrena Taekwondo en ${name} y el cambio ha sido increíble. Ha aprendido disciplina, respeto y autocontrol. Los maestros saben tratar con niños y las clases son seguras. Ya tiene su cinturón amarillo con orgullo.`,
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    trainer: [
      {
        name: "Antonio Pérez",
        rating: 5,
        comment: `${name} es el mejor entrenador personal que he tenido. Diseñó un programa específico para mis objetivos de hipertrofia y en 5 meses he ganado 8kg de músculo. Controla la nutrición, el descanso y ajusta el entrenamiento cada semana según mi progreso.`,
        date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Marta Sánchez",
        rating: 5,
        comment: `Después de años sin hacer ejercicio, ${name} me ayudó a empezar de forma segura. Tiene mucha paciencia, explica cada ejercicio detalladamente y me motiva sin presionar. He perdido 15kg y ganado energía para mi día a día.`,
        date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Carlos Domínguez",
        rating: 4,
        comment: `Profesional muy cualificado con certificaciones NSCA y especialización en rendimiento deportivo. Me preparó para mi primera maratón con un plan periodizado perfecto. Único inconveniente es la alta demanda - hay que reservar con antelación.`,
        date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Raquel Morales",
        rating: 5,
        comment: `${name} transformó mi relación con el ejercicio. Entreno 3 veces por semana con él y los resultados son visibles. Lo que más valoro es su enfoque holístico: entrena cuerpo y mente. Sus sesiones de movilidad han eliminado mi dolor de cuello.`,
        date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    nutritionist: [
      {
        name: "Victoria López",
        rating: 5,
        comment: `${name} cambió mi vida. Después de años de dietas yo-yo, me enseñó a comer bien sin restricciones extremas. Analiza mis analíticas, ajusta macros según mi actividad y hace seguimiento semanal. He perdido 20kg en 8 meses de forma sostenible.`,
        date: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Manuel García",
        rating: 5,
        comment: `Como deportista de competición, necesitaba un nutricionista que entendiera mis necesidades específicas. ${name} diseñó un plan de periodización nutricional que mejoró mi rendimiento un 15%. Mis tiempos de recuperación son mucho mejores.`,
        date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Lucía Fernández",
        rating: 4,
        comment: `Excelente profesional especializada en nutrición hormonal femenina. Entendió mis problemas de SOP y adaptó la alimentación para regular mis ciclos. Consultas completas aunque a veces hay lista de espera. Vale la pena.`,
        date: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Diego Romero",
        rating: 5,
        comment: `${name} no solo me dio un plan de comidas, me educó sobre nutrición. Ahora sé leer etiquetas, entiendo los macronutrientes y cocino de forma saludable. Mi colesterol bajó 40 puntos y me siento con más energía que nunca.`,
        date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ],
    physiotherapist: [
      {
        name: "Eduardo Martín",
        rating: 5,
        comment: `${name} me salvó de una operación de espalda. Después de que 3 traumatólogos me recomendaran cirugía, su tratamiento de fisioterapia avanzada y ejercicio terapéutico eliminó mi hernia L4-L5 en 4 meses. Profesional excepcional.`,
        date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Sandra Núñez",
        rating: 5,
        comment: `Como corredora con fascitis plantar crónica, ${name} fue mi solución. Combina terapia manual, punción seca y ejercicios específicos. En 6 semanas volví a correr sin dolor. Además me enseñó a prevenir futuras lesiones.`,
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Javier Alonso",
        rating: 4,
        comment: `Gran fisioterapeuta deportivo con experiencia en equipos profesionales. Mi recuperación de ligamento cruzado fue más rápida de lo esperado gracias a su protocolo. Clínica bien equipada con Indiba y EPI. Precio acorde a la calidad.`,
        date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      },
      {
        name: "Teresa Blanco",
        rating: 5,
        comment: `${name} trata a toda mi familia. Desde el dolor de hombro de mi marido hasta la escoliosis de mi hija adolescente. Su enfoque es muy completo: evalúa postura, movimiento y hábitos. Los resultados hablan por sí solos.`,
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        verified: true
      }
    ]
  };

  // Default generic testimonials as fallback
  const defaultTestimonials: Testimonial[] = [
    {
      name: "María González",
      rating: 5,
      comment: `Increíble experiencia con ${name}. En 3 meses he conseguido resultados que nunca imaginé posibles. El equipo es muy profesional y la atención personalizada marca la diferencia.`,
      date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      verified: true
    },
    {
      name: "Carlos Ruiz",
      rating: 5,
      comment: `La mejor decisión que he tomado. ${name} se adapta perfectamente a tus necesidades y objetivos. La comunicación es excelente y el seguimiento constante.`,
      date: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
      verified: true
    },
    {
      name: "Laura Martínez",
      rating: 5,
      comment: `Excelente relación calidad-precio. El ambiente es motivador y los servicios son variados. He mejorado notablemente y lo recomiendo sin dudas.`,
      date: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
      verified: true
    },
    {
      name: "Javier López",
      rating: 4,
      comment: `Muy profesional y atento. Solo echo de menos más disponibilidad horaria, pero por lo demás la experiencia ha sido muy positiva.`,
      date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      verified: true
    }
  ];

  // Normalize category name
  const normalizedCategory = category?.toLowerCase().replace(/[^a-z]/g, '_') || '';
  
  return testimonialsByCategory[normalizedCategory] || defaultTestimonials;
};

export const Testimonials = ({ testimonials, category, profileName }: TestimonialsProps) => {
  const categoryTestimonials = getCategoryTestimonials(category, profileName);
  const displayTestimonials = testimonials && testimonials.length > 0 ? testimonials : categoryTestimonials;
  if (displayTestimonials.length === 0) return null;

  const averageRating = displayTestimonials.reduce((sum, t) => sum + t.rating, 0) / displayTestimonials.length;

  return (
    <Card className="border-2 border-primary/20 shadow-lg">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            <span className="text-xl">Lo Que Dicen Nuestros Clientes</span>
          </span>
          <div className="flex items-center gap-2 bg-background/60 px-4 py-2 rounded-full">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            <span className="text-lg font-bold">{averageRating.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">
              ({displayTestimonials.length} reseñas verificadas)
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {displayTestimonials.map((testimonial, index) => (
            <div key={index} className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{testimonial.name}</p>
                    {testimonial.verified && (
                      <Badge variant="secondary" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verificado
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(testimonial.date), "d 'de' MMMM, yyyy", { locale: es })}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < testimonial.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                "{testimonial.comment}"
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
